import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { unix } from 'moment';
import { env } from '../../../../env';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { useAppDispatch, useAppSelector } from '../../../../store';
import {
  clearPaymentData,
  getClientSecretThunk,
  getPaymentMethods,
  getPaymentsHistory,
  removePaymentMethod,
  selectPayment,
  setPaymentMethodDefault,
} from '../../api/PaymentSlice';
import { selectAccountState } from '../../../account/api/accountSlice';

// COMPONENTS
import { Spinner } from 'react-bootstrap';
import alert from '../../../../utils/alert';
import deleteIcon from '../../../../assets/images/icons/trash-red-icon.svg';
import ConfirmationModal from '../../../app/components/ConfirmationModal';
import StripeAddCard from '../../../get-care/Components/select-payment-method/StripeAddCard';

// UTILS
import cards from '../../../../utils/cards';

// TYPES
import { PaymentHistoryResponse, PaymentMethodsResponse } from '../../types';
import i18next from 'i18next';

const stripePromise = loadStripe(env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function PaymentsScreen() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    paymentMethods,
    paymentsHistory,
    isLoading,
    isPaymentsHistoryLoading,
    isSetPaymentMethodDefaultLoading,
    paymentsHistoryHasMore,
    isRemoveCardLoading,
  } = useAppSelector(selectPayment);
  const { patient } = useAppSelector(selectAccountState);
  const [clientSecret, setClientSecret] = useState<string>();
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(false);
  const [showRemoveCardModal, setShowRemoveCardModal] = useState<boolean>(false);
  const [cardToBeDeleted, setCardToBeDeleted] = useState<string>();
  const historyListRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    dispatch(clearPaymentData());
  }, [dispatch]);

  const options = useMemo<StripeElementsOptions>(
    () => ({
      clientSecret: clientSecret,
      locale: i18next.language as StripeElementsOptions['locale'],
    }),
    [clientSecret],
  );

  const getClientSecret = useCallback(async () => {
    if (patient?.id) {
      const { resource } = await dispatch(getClientSecretThunk(patient?.id)).unwrap();
      if (resource?.setupIntent) {
        setClientSecret(resource?.setupIntent);
      }
    }
  }, [dispatch, patient?.id]);

  useEffect(() => {
    if (patient?.id) {
      dispatch(getPaymentMethods(patient.id));
      dispatch(getPaymentsHistory({ patientID: patient.id, limit: 10, offset: 0 }));
    }
    getClientSecret();
  }, [dispatch, patient?.id, getClientSecret, patient]);

  const loadMorePaymentHistory = useCallback(() => {
    if (!isPaymentsHistoryLoading && paymentsHistoryHasMore && patient?.id) {
      dispatch(
        getPaymentsHistory({
          patientID: patient.id,
          limit: 10,
          offset: paymentsHistory.length,
        }),
      );
    }
  }, [
    dispatch,
    patient?.id,
    paymentsHistory.length,
    isPaymentsHistoryLoading,
    paymentsHistoryHasMore,
  ]);

  const handleScroll = useCallback(
    (event: Event) => {
      const target = event.target as HTMLUListElement;
      if (target) {
        const { scrollTop, scrollHeight, clientHeight } = target;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
          loadMorePaymentHistory();
        }
      }
    },
    [loadMorePaymentHistory],
  );

  useEffect(() => {
    const listElement = historyListRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (listElement) {
        listElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  const setAsDefaultButtonClick = async (paymentMethodId: string) => {
    try {
      if (patient?.id) {
        await dispatch(setPaymentMethodDefault({ patientID: patient.id, paymentMethodId }));
        await dispatch(getPaymentMethods(patient.id));

        alert.success(t('Your default card has been updated.'));
      }
    } catch (e) {
      alert.error(t('Something went wrong. Please try again.'));
    }
    if (patient?.id) {
      dispatch(getPaymentMethods(patient?.id));
    }
  };

  const RenderCard = ({ paymentMethod }: { paymentMethod: PaymentMethodsResponse }) => {
    return (
      <>
        <div key={paymentMethod.paymentMethodID} className='mb-4'>
          <div role='listitem' className='card h-100 shadow-sm'>
            <div className='card-body d-flex flex-column'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <img
                  src={cards[paymentMethod.brand as keyof typeof cards]}
                  alt='card logo'
                  height={34}
                  className='me-2'
                  style={{ borderRadius: 8 }}
                />
                <div className='d-flex flex-row'>
                  <button
                    className='btn text-primary p-0 me-3'
                    onClick={() => {
                      setCardToBeDeleted(paymentMethod.paymentMethodID);
                      setShowRemoveCardModal(true);
                    }}
                    style={{ marginTop: -2 }}
                  >
                    <img src={deleteIcon} width={20} height={20} alt='delete-img' />
                  </button>
                  {paymentMethod.default ? (
                    <p className='badge btn text-primary bg-primary-100 m-0'>{t('Default')}</p>
                  ) : (
                    <button
                      className='btn text-primary p-0 fw-semibold'
                      style={{ fontSize: 12 }}
                      onClick={() => setAsDefaultButtonClick(paymentMethod.paymentMethodID)}
                    >
                      {paymentMethod.default ? t('Default') : t('Set as Default')}
                    </button>
                  )}
                </div>
              </div>
              <h6
                className='card-title'
                style={{ fontSize: 14 }}
              >{`${t('Ending in')} - ${paymentMethod.last4}`}</h6>
              <p className='card-text text-muted mt-auto' style={{ fontSize: 14 }}>
                {t('Expiry')}: {paymentMethod.expMonth}/{paymentMethod.expYear}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  };

  const RenderPaymentHistory = ({ payment }: { payment: PaymentHistoryResponse }) => {
    const formattedAmount = (payment.amountReceived / 100).toFixed(2);

    return (
      <li className='list-group-item d-flex justify-content-between align-items-center'>
        <div>
          <p>{unix(payment.paymentCreatedAt).format('MMM DD, YYYY')}</p>
          <p className='card-text text-muted' style={{ fontSize: 14 }}>
            {payment.description}
          </p>
        </div>
        <h6 className='card-title'>$ {formattedAmount}</h6>
      </li>
    );
  };

  const onCloseAddCardForm = (shouldRefresh: boolean) => {
    setShouldShowModal(false);
    if (shouldRefresh && patient) {
      dispatch(getPaymentMethods(patient.id));
      getClientSecret();
    }
  };

  const handleRemoveCardConfirm = async () => {
    setShowRemoveCardModal(false);
    try {
      if (cardToBeDeleted && patient) {
        await dispatch(
          removePaymentMethod({ patientID: patient.id, paymentMethodId: cardToBeDeleted }),
        );
      }
      alert.success(t('Your card has been removed successfully.'));
      if (patient) {
        await dispatch(getPaymentMethods(patient.id));
      }
    } catch (e) {
      alert.error(t('Something went wrong. Please try again.'));
    } finally {
      setCardToBeDeleted(undefined);
    }
  };

  if (isLoading || isSetPaymentMethodDefaultLoading || isRemoveCardLoading) {
    return (
      <div className='spinner-container'>
        <Spinner className='spinner-icon' />
      </div>
    );
  }

  const filteredPayments = paymentsHistory.filter(
    (payment, index, self) =>
      payment.amountReceived !== 0 && index === self.findIndex((t) => t.id === payment.id),
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <>
        <div className='row'>
          <div className='col-12 col-lg-5 mb-3'>
            <div className='d-flex flex-row justify-content-between'>
              <h6 className='mb-3'>{t('Payment Methods')}</h6>
              <button
                className='p-0 h-100 page__subsection-link text-primary-700'
                onClick={() => setShouldShowModal(true)}
                type='button'
              >
                {t('Add new credit card')}
              </button>
            </div>
            <div className='row'>
              {paymentMethods.length !== 0 ? (
                <>
                  {paymentMethods &&
                    paymentMethods.map((paymentMethod: PaymentMethodsResponse) => (
                      <div key={paymentMethod.paymentMethodID}>
                        <RenderCard paymentMethod={paymentMethod} />
                      </div>
                    ))}
                </>
              ) : (
                <div style={{ fontSize: 14 }}>
                  <p className='mb-0' style={{ color: '#495057' }}>
                    {t('No card on file.')}
                  </p>
                  <p style={{ color: '#6c757d' }}>
                    {t('To start get started, tap on the “Add new credit card” button.')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className='col-12 col-lg-7'>
            <h6 className='mb-3'>{t('Payment History')}</h6>
            {filteredPayments.length !== 0 ? (
              <ul
                className='list-group'
                style={{ height: 320, overflow: 'auto' }}
                ref={historyListRef}
              >
                {filteredPayments.map((payment: any) => (
                  <RenderPaymentHistory key={payment.id} payment={payment} />
                ))}
                {isPaymentsHistoryLoading && (
                  <li className='list-group-item text-center'>
                    <Spinner animation='border' size='sm' />
                  </li>
                )}
              </ul>
            ) : (
              <p>{t('No payment history.')}</p>
            )}
          </div>
        </div>
        <StripeAddCard
          shouldShowModel={shouldShowModal}
          stripePromise={stripePromise}
          options={options}
          onCloseAddCardForm={onCloseAddCardForm}
        />
        <ConfirmationModal
          showModal={showRemoveCardModal}
          onHide={() => setShowRemoveCardModal(false)}
          onConfirm={() => handleRemoveCardConfirm()}
          title={t('Are you sure you want to remove this card?')}
          bodyText=''
          confirmButtonText={t('Confirm')}
          cancelButtonText={t('Cancel')}
        />
      </>
    </div>
  );
}

export default PaymentsScreen;
